import type { RouteLocationRaw } from "vue-router";

import { EntityType } from "@/lib/openapi/gen/types.gen";

/**
 * Where an entity lives, given only its type and id.
 *
 * The ask box's answers carry references as bare `{entity_type, entity_id}`
 * pairs, and nothing else in the app has needed to turn that into a URL: every
 * other entity link is built by a component that already knows which kind of
 * thing it is holding. This is the one place that maps the whole vocabulary.
 *
 * Route names and param names are router facts, which is why this sits beside
 * `page-key.ts` rather than with the component that calls it — and why it is
 * pure, with no store or composable reachable from here.
 *
 * Deliberately NOT built on `sync-store.ts`'s tables, though they look similar.
 * `identityPerEntity` there is derived from the *sync API's request bodies* and
 * covers only syncable types (no `domain_term`, no `artefact`); teaching it
 * about `domain_term` would be claiming that a glossary term is syncable, which
 * it is not. And `pagePerEntityAttribute` is keyed
 * `entityType -> attributeName -> routeName` because a failed sync is a failed
 * *field* write — a reference has no attribute, so flattening it would mean
 * picking an arbitrary key.
 *
 * Note this is a second copy of knowledge the backend also holds, in
 * `bekend/src/infrastructure/repo/search/search_repository.py` (which builds
 * finished links for search results, and is where OmniBox's links come from).
 * The two cover different type sets and neither subsumes the other; if you
 * change a route segment, change both.
 */

/** A route name plus the param key its id goes in. */
type EntityTarget = { name: string; idParam: string };

/**
 * The element route takes the type as a *param* rather than having one route
 * per kind, so these four share a target that cannot be expressed as
 * `{name, idParam}`.
 */
const ELEMENT_TYPES: ReadonlySet<string> = new Set([
    EntityType.TASK,
    EntityType.GATEWAY,
    EntityType.EVENT,
    EntityType.FLOW,
]);

/**
 * Every entity type, and where it goes — or `null` where it goes nowhere.
 *
 * Typed against the generated enum on purpose. `null` is then a decision that
 * somebody wrote down, and the day the backend adds an eighteenth type this
 * stops compiling until someone decides what to do with it. A `Partial<>` or a
 * plain object would let a new type fall through as "unlinkable" silently,
 * which is exactly the failure this table exists to prevent.
 *
 * The backend gate validates references against the *whole* enum, not the
 * shorter list the ask prompt offers, so the `null` rows are reachable rather
 * than theoretical.
 */
const TARGETS: Record<EntityType, EntityTarget | null> = {
    [EntityType.DOMAIN]: { name: "domainPage", idParam: "domainId" },
    [EntityType.PRODUCT]: { name: "productPage", idParam: "productId" },
    [EntityType.ROLE]: { name: "rolePage", idParam: "roleId" },
    [EntityType.BUSINESS_ENTITY]: {
        name: "businessEntityPage",
        idParam: "businessEntityId",
    },
    [EntityType.DOMAIN_TERM]: {
        name: "domainTermPage",
        idParam: "domainTermId",
    },
    [EntityType.PROCESS]: { name: "processPage", idParam: "processId" },
    [EntityType.APPLICATION]: {
        name: "applicationPage",
        idParam: "applicationId",
    },
    // Two enum values, one page: a document *is* an artefact, and the route
    // takes `artefactId` for both.
    [EntityType.DOCUMENT]: { name: "documentPage", idParam: "artefactId" },
    [EntityType.ARTEFACT]: { name: "documentPage", idParam: "artefactId" },

    // Handled by ELEMENT_TYPES above — the entries exist so the Record stays
    // exhaustive and a reader sees that these were not forgotten.
    [EntityType.TASK]: null,
    [EntityType.GATEWAY]: null,
    [EntityType.EVENT]: null,
    [EntityType.FLOW]: null,

    // Genuinely unlinkable, each for its own reason.
    //
    // An application entity's page is nested under its application, so the URL
    // needs a parent id that a `{type, id}` pair does not carry.
    [EntityType.APPLICATION_ENTITY]: null,
    // Artefact groups are a container in the tree; nothing routes to one.
    [EntityType.ARTEFACT_GROUP]: null,
    // These two are addressed by *name* in the URL rather than by id — they are
    // the `{orgName}/steks/{stekName}` prefix itself, not something underneath
    // it. An answer referring to the stek you are already standing in has
    // nowhere useful to send you anyway.
    [EntityType.ORGANIZATION]: null,
    [EntityType.STEK]: null,
};

/**
 * Every value the enum has — *recognised*, which is not the same as linkable.
 * Four of these resolve to `null` in the table above. This set only narrows the
 * `string` the wire gives us to something the table can be keyed by; whether
 * there is anywhere to go is the table's answer, not this one's.
 */
const KNOWN_TYPES: ReadonlySet<string> = new Set(Object.values(EntityType));

/** The stek a link is being built inside, as the URL spells it. */
export type StekScope = {
    orgName: string | string[] | undefined;
    stekName: string | string[] | undefined;
};

const segment = (value: string | string[] | undefined): string | undefined =>
    typeof value === "string" && value ? value : undefined;

/**
 * A route for this reference, or `undefined` when there isn't one.
 *
 * `undefined` rather than a throw: `entity_type` arrives as a bare string from
 * a language model, and one unrecognised value must cost that one link, not the
 * whole answer.
 *
 * Callers are expected to wrap the result in `withThreadRevision` themselves —
 * that is the house convention, and pulling it in here would drag the release
 * store (and through it `useDialog`) into a pure function.
 *
 * The `:version?` segment every target route ends with is deliberately not
 * filled: a reference should land on the entity as it stands, not on whatever
 * historical version the reader happens to have open.
 */
export const entityLocation = (
    reference: { entity_type: string; entity_id: string },
    scope: StekScope
): RouteLocationRaw | undefined => {
    const orgName = segment(scope.orgName);
    const stekName = segment(scope.stekName);
    // Reachable, not defensive: the box is mounted from the application layout,
    // which also serves the org-level pages where there is no stek in the URL
    // at all — and `:orgName?` is optional, so it can be an empty string.
    if (!orgName || !stekName) {
        return undefined;
    }
    if (!reference.entity_id || !KNOWN_TYPES.has(reference.entity_type)) {
        return undefined;
    }

    const entityType = reference.entity_type as EntityType;

    if (ELEMENT_TYPES.has(entityType)) {
        return {
            name: "processElementPage",
            params: {
                orgName,
                stekName,
                // The kind of element is a path segment here, which is why
                // there is no `taskId`/`eventId` param and no parent process id
                // — the element route addresses it directly.
                elementType: entityType,
                elementId: reference.entity_id,
            },
        };
    }

    const target = TARGETS[entityType];
    if (!target) {
        return undefined;
    }
    return {
        name: target.name,
        params: {
            orgName,
            stekName,
            [target.idParam]: reference.entity_id,
        },
    };
};
